import * as ko from 'knockout';
import * as localisationHelper from 'localisationHelper';

import "Content/ts/Shared/KnockoutBindings/Bootstrap/bootstrapGridViewBindings";

// Components
import 'Content/ts/Shared/Inputs/inputs-base';
import 'Content/ts/Shared/Inputs/Email/input-email';
import 'Content/ts/Shared/Inputs/Phone/input-phone';
import 'Content/ts/Shared/Address/country-selector';
import 'Content/ts/Shared/Buttons/StandardButtons/Primary/button-primary';
import "@legend/web-components/input";
import "Content/ts/Shared/Inputs/TextButtonGroups/input-text-button-group";

// Models
import { IBillingDetailsMapped } from 'Content/ts/Models/Contact/Details/billingDetails';
import { PostCodeLookupAddress } from 'Content/ts/Models/Joining/PostCodeLookupAddress';
import { SystemConfigurationModel } from 'Content/ts/Models/Shared/SystemConfigurationModel';

// Services
import { PostCodeLookupService } from 'Content/ts/Shared/Services/PostCode/postcodeLookupService';
import { ValidationService } from 'Content/ts/Shared/Services/Validation/validationService';
import { PostcodeLookupConfiguration } from '@legend/service/external/address';
import { AddressServiceInstance } from "@legend/service/address";

export interface BillingDetailsCaptureParams {
    ContactDetails: IBillingDetailsMapped;
    ValidationFields: KnockoutObservable<any>[];
}

export default class BillingDetailsCapture {
    ContactDetails: IBillingDetailsMapped;
    CustomMessages: {
        FirstNameLabel: string;
        LastNameLabel: string;
        EmailLabel: string;
        PhoneLabel: string;
        PostCodeLabel: string;
        AddressLine1Label: string;
        CityLabel: string;
        RegionLabel: string;
        FindYourAddress: string;
        SelectedAddressLabel: string;
        DefaultPostcodeErrorMessage: string;
        AddressNotFound: string;
        PostCodeMinLength: string;
        HouseNameOrNumber: string;
    };

    PostCodeLoading: KnockoutObservable<boolean>;
    PostcodeLookupFailed: KnockoutObservable<boolean>;
    PostcodeLookupResults: KnockoutObservableArray<any>;
    NoAddressFound: KnockoutObservable<boolean>;
    SelectedAddress: KnockoutObservable<PostCodeLookupAddress>;
    ShowSelectedAddress: KnockoutComputed<boolean>;
    PostCodeLookupEnabled: KnockoutObservable<boolean>;
    AddressChosen: KnockoutObservable<boolean>;

    postcodeLookupConfiguration: KnockoutObservable<PostcodeLookupConfiguration>;
    houseNameOrNumber: KnockoutObservable<string>;

    PostCodeMinLength: number;

    ValidationFields: KnockoutObservable<any>[];

    constructor(params: BillingDetailsCaptureParams) {
        this.ContactDetails = params.ContactDetails;
        this.ValidationFields = params.ValidationFields;

        this.PostCodeLoading = ko.observable(false);
        this.PostcodeLookupFailed = ko.observable(false);
        this.NoAddressFound = ko.observable(false);
        this.PostCodeLookupEnabled = ko.observable(false);
        this.SelectedAddress = ko.observable(null);
        this.PostcodeLookupResults = ko.observableArray([]);
        this.AddressChosen = ko.observable(true);
        this.postcodeLookupConfiguration = ko.observable({
            HouseNameOrNumberRequired: false
        });
        this.houseNameOrNumber = ko.observable("");

        this.PostCodeMinLength = 5;

        this.SetPostCodeLookup();
        this.RetrieveCustomMessages();
        this.SetupComputeds();
        this.SetupSubscriptions();
        this.SetupAdditionalValidation();
    }

    SetPostCodeLookup() {
        PostCodeLookupService.PostcodeLookupEnabled()
            .then((response: SystemConfigurationModel) => {
                this.PostCodeLookupEnabled(JSON.parse(response.SettingValue));
            });
        AddressServiceInstance.getPostcodeLookupConfiguration().then((response) => {
            this.postcodeLookupConfiguration(response);

            if (this.postcodeLookupConfiguration().HouseNameOrNumberRequired) {
                this.houseNameOrNumber.extend({
                    required: {
                        params: true,
                        message: localisationHelper.getString("GenericResources", "Address_HouseNameOrNumberRequiredLookupValidation")
                    }
                });
            }
        });
    };

    SearchPostCode(): void {
        if (this.postcodeLookupConfiguration().HouseNameOrNumberRequired && !this.houseNameOrNumber.isValid()) {
            this.houseNameOrNumber.isModified(true);
            return;
        }

        this.PostCodeLoading(true);
        this.PostcodeLookupFailed(false);


        PostCodeLookupService.PostCodeSearch(this.houseNameOrNumber(), this.ContactDetails.PostCode())
            .then((response: any) => {
                if (response.Success) {
                    this.PostcodeLookupResults(response.Addresses);
                    if (!this.PostcodeLookupResults().length) {
                        this.NoAddressFound(true);
                    } else {
                        this.NoAddressFound(false);
                        this.AddressChosen(false);
                    }
                    ValidationService.HideValidationMessage(this.SelectedAddress);
                } else {
                    this.PostcodeLookupFailed(true);
                }
            }).finally(() => {
                this.PostCodeLoading(false);
            });;
    };

    private RetrieveCustomMessages() {
        this.CustomMessages = {
            FirstNameLabel: localisationHelper.getString("AccountResources", "Register_FirstName"),
            LastNameLabel: localisationHelper.getString("AccountResources", "Register_Surname"),
            EmailLabel: localisationHelper.getString("AccountResources", "Register_Email"),
            PhoneLabel: localisationHelper.getString("AccountResources", "Phone_Number"),
            PostCodeLabel: localisationHelper.getString("AccountResources", "Register_Postcode"),
            AddressLine1Label: localisationHelper.getString("AccountResources", "Register_Address"),
            CityLabel: localisationHelper.getString("AccountResources", "Register_City"),
            RegionLabel: localisationHelper.getString("AccountResources", "Register_County"),
            FindYourAddress: localisationHelper.getString("GenericResources", "FindYourAddress"),
            SelectedAddressLabel: localisationHelper.getString("AccountResources", "Joining_SelectAddress"),
            DefaultPostcodeErrorMessage: localisationHelper.getString("ErrorMessageResources", "EditProfile_UnableToLocatePostcodeDetails"),
            AddressNotFound: localisationHelper.getString("PersonalProfileResources", "AddressNotFound"),
            PostCodeMinLength: localisationHelper.getString("ValidationMessageResources", "FieldHasMinLength")
                .replace("{name}", "Postcode").replace("{length}", this.PostCodeMinLength.toString()),
            HouseNameOrNumber: localisationHelper.getString("GenericResources", "Address_HouseNameOrNumber")
        }
    };

    private SetupComputeds() {
        this.ShowSelectedAddress = ko.computed(() => {
            return this.PostcodeLookupResults().length > 0 && !this.AddressChosen();
        });
    };

    private SetupSubscriptions() {
        const fieldMaxLength = 50;

        this.SelectedAddress.subscribe((value: PostCodeLookupAddress) => {
            if (value) {
                this.ContactDetails.AddressLine1(value.Address1.substring(0, fieldMaxLength));
                this.ContactDetails.City(value.City.substring(0, fieldMaxLength));
                this.ContactDetails.PostCode(value.PostCode.substring(0, fieldMaxLength));
                this.ContactDetails.Region(value.County.substring(0, fieldMaxLength));

                this.AddressChosen(true);
            }
        });
    };

    private SetupAdditionalValidation() {
        ValidationService.AddValidationRule(this.ContactDetails.PostCode, this.ValidationFields, {
            minLength: {
                params: this.PostCodeMinLength,
                message: this.CustomMessages.PostCodeMinLength
            }
        });
    };

    dispose() {
        ValidationService.RemoveValidationRule(this.ContactDetails.PostCode, this.ValidationFields);
    }
}

var componentName = 'billing-details-capture';
ko.components.unregister(componentName);
ko.components.register(componentName, {
    viewModel: BillingDetailsCapture,
    template: { require: `text!/Themes/Default/Content/ts/Shared/BillingDetails/${componentName}.html` }
});